import { BULLION_BONUS_TIERS, BULLION_CHIPS_PER_CLICK, BULLION_GROUP_CHIP_LIMIT, BULLION_GROUP_COUNT } from "./constants";
import { regularChipsInGroup, bullionSelectionStorageKey } from "./roomUtils";
import type { BullionBonusPick, BullionBonusPicks, BullionMarket, BullionRoom, BullionSelectionMap } from "./types";

function rand(n: number): number {
    return Math.floor(Math.random() * n);
}

function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i -= 1) {
        const j = rand(i + 1);
        [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
}

/** k positive integers that sum to `sum` (each >= 1). */
function randomComposition(sum: number, k: number): number[] {
    if (k <= 0 || sum < k) return [];
    if (k === 1) return [sum];
    const cuts = new Set<number>();
    while (cuts.size < k - 1) {
        cuts.add(1 + rand(sum - 1));
    }
    const sorted = [0, ...Array.from(cuts).sort((a, b) => a - b), sum];
    const out: number[] = [];
    for (let i = 0; i < sorted.length - 1; i += 1) {
        out.push(sorted[i + 1]! - sorted[i]!);
    }
    return out;
}

function luckyDipMarketNameKey(name: string): string {
    return String(name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

/** Deduped markets per group (same rules as full Lucky Dip). */
function uniqueMarketsForBullionGroup(room: BullionRoom, groupIndex: number): BullionMarket[] {
    const markets = (room.groups[groupIndex] || []).filter((m) => m.runners.length > 0);
    const uniqueByKey = new Map<string, BullionRoom["groups"][number][number]>();
    const seenNames = new Set<string>();
    for (const market of markets) {
        const storageKey = bullionSelectionStorageKey(market);
        const nameKey = luckyDipMarketNameKey(market.name);
        if (nameKey) {
            if (seenNames.has(nameKey)) continue;
            seenNames.add(nameKey);
        }
        if (!uniqueByKey.has(storageKey)) uniqueByKey.set(storageKey, market);
    }
    return Array.from(uniqueByKey.values());
}

function cloneBullionSelections(picks: BullionSelectionMap): BullionSelectionMap {
    const out: BullionSelectionMap = {};
    for (const [k, v] of Object.entries(picks)) {
        out[k] = { ...v };
    }
    return out;
}

function bonusPickStillValid(bonus: BullionBonusPick | null, picks: BullionSelectionMap): boolean {
    if (!bonus) return false;
    const sel = picks[bonus.marketId];
    return (
        sel != null &&
        sel.runnerId === bonus.runnerId &&
        sel.groupIndex === bonus.groupIndex &&
        sel.chips > 0
    );
}

/** Build up to `BULLION_BONUS_TIERS.length` bonuses on distinct chip-bearing rows, preferring already-valid ones. */
function assignLuckyBonusPicks(picks: BullionSelectionMap, existing: BullionBonusPicks = []): BullionBonusPicks {
    const candidates = Object.values(picks).filter((p) => p.chips > 0);
    if (candidates.length === 0) return [];
    const usedKeys = new Set<string>();
    const out: BullionBonusPicks = [];
    for (const { tier, points } of BULLION_BONUS_TIERS) {
        const kept = existing.find((b) => b.tier === tier && bonusPickStillValid(b, picks) && !usedKeys.has(b.marketId));
        if (kept) {
            usedKeys.add(kept.marketId);
            out.push({ ...kept, points });
            continue;
        }
        const pool = shuffle(candidates.filter((c) => !usedKeys.has(c.marketId)));
        const chosen = pool[0];
        if (!chosen) continue;
        usedKeys.add(chosen.marketId);
        out.push({
            tier,
            points,
            marketId: chosen.marketId,
            runnerId: chosen.runnerId,
            groupIndex: chosen.groupIndex,
        });
    }
    return out;
}

export type LuckyDipResult = {
    picks: BullionSelectionMap;
    bonusPicks: BullionBonusPicks;
};

/**
 * Random stake fill: `BULLION_GROUP_CHIP_LIMIT` regular bars per available category
 * (up to `BULLION_REGULAR_CHIPS` total) plus the +450 bullion bonus.
 * Returns null only when no category has any market with runners.
 */
export function buildLuckyDipSelections(room: BullionRoom): LuckyDipResult | null {
    const selections: BullionSelectionMap = {};
    const groupsWithMarkets: number[] = [];
    const uniqueMarketsByGroup = new Map<number, BullionRoom["groups"][number]>();

    for (let g = 0; g < BULLION_GROUP_COUNT; g += 1) {
        const uniqueMarkets = uniqueMarketsForBullionGroup(room, g);
        uniqueMarketsByGroup.set(g, uniqueMarkets);
        if (uniqueMarkets.length > 0) groupsWithMarkets.push(g);
    }
    if (groupsWithMarkets.length === 0) return null;

    for (const g of groupsWithMarkets) {
        const markets = uniqueMarketsByGroup.get(g) || [];
        const k = 1 + rand(Math.min(BULLION_GROUP_CHIP_LIMIT, markets.length));
        const chosen = shuffle(markets).slice(0, k);
        const parts = randomComposition(BULLION_GROUP_CHIP_LIMIT, k);
        if (parts.length !== k) return null;

        for (let i = 0; i < k; i += 1) {
            const market = chosen[i]!;
            const runner = market.runners[rand(market.runners.length)]!;
            const chips = parts[i]! * BULLION_CHIPS_PER_CLICK;
            if (chips <= 0) continue;
            const pickKey = bullionSelectionStorageKey(market);
            const existing = selections[pickKey];
            if (!existing) {
                selections[pickKey] = {
                    marketId: pickKey,
                    runnerId: runner.id,
                    chips,
                    groupIndex: g,
                    marketName: market.name,
                    selectionName: runner.name,
                };
                continue;
            }

            // Defensive merge for rare feed duplicates sharing one storage key.
            existing.chips += chips;
        }
    }

    /** Bonuses must land on existing regular bets (rows with chips), on distinct rows. */
    const bonusPicks = assignLuckyBonusPicks(selections);
    if (bonusPicks.length === 0) return null;
    return { picks: selections, bonusPicks };
}

/**
 * “Auto Fill” in the UI: like full Lucky Dip, but keeps existing regular chips and only randomises the remainder
 * per category (and assigns gold if missing — preserves an existing gold pick when it still sits on a staked line).
 */
export function buildLuckyDipRemainingSelections(
    room: BullionRoom,
    existingPicks: BullionSelectionMap,
    existingBonuses: BullionBonusPicks
): LuckyDipResult | null {
    const selections = cloneBullionSelections(existingPicks);

    for (let g = 0; g < BULLION_GROUP_COUNT; g += 1) {
        const placed = regularChipsInGroup(selections, g);
        const d = BULLION_GROUP_CHIP_LIMIT - placed;
        if (d <= 0) continue;

        const markets = uniqueMarketsForBullionGroup(room, g);
        if (markets.length === 0) return null;

        const k = 1 + rand(Math.min(d, markets.length));
        const parts = randomComposition(d, k);
        if (parts.length !== k) return null;

        const chosen = shuffle(markets).slice(0, k);
        for (let i = 0; i < k; i += 1) {
            const market = chosen[i]!;
            const pickKey = bullionSelectionStorageKey(market);
            const existingRow = selections[pickKey];
            const runner =
                existingRow && market.runners.some((r) => r.id === existingRow.runnerId)
                    ? market.runners.find((r) => r.id === existingRow.runnerId)!
                    : market.runners[rand(market.runners.length)]!;
            const chips = parts[i]! * BULLION_CHIPS_PER_CLICK;
            if (chips <= 0) continue;
            const existing = selections[pickKey];
            if (!existing) {
                selections[pickKey] = {
                    marketId: pickKey,
                    runnerId: runner.id,
                    chips,
                    groupIndex: g,
                    marketName: market.name,
                    selectionName: runner.name,
                };
                continue;
            }
            existing.chips += chips;
        }
    }

    const bonusPicks = assignLuckyBonusPicks(selections, existingBonuses);
    if (bonusPicks.length === 0) return null;
    return { picks: selections, bonusPicks };
}
